"use client";

import { StyleProvider } from "@ant-design/cssinjs";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import { ConfigProvider } from "antd";
import { useEffect, type ReactNode } from "react";
import { themeFor, type ResolvedTheme } from "@/theme/antd-theme";
import { THEME_PENDING_ATTRIBUTE } from "@/theme/theme-config";
import { useThemeMode, wantedTheme } from "@/theme/theme-mode";

// the theme the static `message.*` / `notification.*` roots render with
let staticTheme: ResolvedTheme = "light";

// Static `message.*` / `notification.*` calls render in their own React root,
// outside this tree. Give that root the same theme (and cascade layer) so
// toasts match the rest of the app instead of falling back to antd defaults.
ConfigProvider.config({
	holderRender: (children) => (
		<StyleProvider layer>
			<ConfigProvider theme={themeFor(staticTheme)}>{children}</ConfigProvider>
		</StyleProvider>
	),
});

/** antd's light or dark theme for the tree, with <html> kept in step. */
const ThemedConfig = ({ children }: { children: ReactNode }) => {
	const { resolved } = useThemeMode();
	useEffect(() => {
		staticTheme = resolved;
		// hydration always renders light; wait for the render with the choice
		if (resolved !== wantedTheme()) return;
		const root = document.documentElement;
		root.setAttribute("data-theme", resolved);
		// the dark tokens are on screen now: show the page (theme-config.ts)
		root.removeAttribute(THEME_PENDING_ATTRIBUTE);
	}, [resolved]);
	return <ConfigProvider theme={themeFor(resolved)}>{children}</ConfigProvider>;
};

/**
 * antd styling root.
 * - AntdRegistry writes antd's CSS into the prerendered HTML, so the first
 *   paint is already styled (the app is fully static; without this the
 *   inputs and buttons render unstyled until JavaScript runs).
 * - `layer` puts antd's CSS in `@layer antd` (order declared in globals.css),
 *   above Tailwind's preflight and below its utilities, so layout utilities
 *   such as `w-full` or `mb-0` on an antd component take effect.
 * - The theme (light / dark / system) is the viewer's choice: theme-mode.ts.
 */
const AntdProvider = ({ children }: { children: ReactNode }) => (
	<AntdRegistry layer>
		<ThemedConfig>{children}</ThemedConfig>
	</AntdRegistry>
);

export default AntdProvider;
